import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiTrash2, FiMessageCircle, FiPhone, FiMail, FiUser, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { contactAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 15;

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await contactAPI.getAll();
        const data = response.data?.data?.contacts || [];
        setContacts(data);
      } catch {
        toast.error('Failed to load contact messages.');
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFiltered(contacts);
    } else {
      setFiltered(contacts.filter((c) =>
        c.name?.toLowerCase().includes(q) ||
        c.phone?.includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.message?.toLowerCase().includes(q)
      ));
    }
    setPage(1);
  }, [search, contacts]);

  const handleWhatsApp = async (contact) => {
    // Mark as read when admin opens WhatsApp
    if (!contact.isRead) {
      try {
        await contactAPI.markAsRead(contact._id);
        setContacts((prev) => prev.map((c) => c._id === contact._id ? { ...c, isRead: true } : c));
      } catch {}
    }
    // Clean phone: remove spaces, dashes, parentheses; keep leading +
    const cleaned = contact.phone.replace(/[\s\-().]/g, '');
    window.open(`https://wa.me/${cleaned}`, '_blank', 'noopener,noreferrer');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    setDeletingId(id);
    try {
      await contactAPI.delete(id);
      setContacts((prev) => prev.filter((c) => c._id !== id));
      toast.success('Message deleted.');
    } catch {
      toast.error('Failed to delete message.');
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const unreadCount = contacts.filter((c) => !c.isRead).length;

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-serif font-bold text-primary-500">Contact Messages</h2>
          <p className="text-gray-500 text-sm mt-1">Messages submitted through the website contact form</p>
        </div>
        {unreadCount > 0 && (
          <span className="px-3 py-1.5 bg-secondary-100 text-secondary-700 border border-secondary-200 rounded-full text-sm font-semibold">
            {unreadCount} unread
          </span>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 flex gap-4 items-center">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by name, phone, email or message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div className="text-sm text-gray-500 flex-shrink-0">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">Name</th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">Phone</th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden md:table-cell">Email</th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden lg:table-cell">Message</th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden sm:table-cell">Date</th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((contact, idx) => (
                  <motion.tr
                    key={contact._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.04 }}
                    className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${!contact.isRead ? 'bg-blue-50/30' : ''}`}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {!contact.isRead && (
                          <span className="w-2 h-2 bg-secondary-500 rounded-full flex-shrink-0" title="Unread" />
                        )}
                        <div className="w-8 h-8 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0">
                          <FiUser className="w-4 h-4 text-primary-400" />
                        </div>
                        <span className="font-medium text-gray-800">{contact.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-gray-700">
                        <FiPhone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        {contact.phone}
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-gray-500">
                      {contact.email ? (
                        <div className="flex items-center gap-1.5">
                          <FiMail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          {contact.email}
                        </div>
                      ) : (
                        <span className="text-gray-300 italic">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-gray-600 max-w-xs">
                      <p className="truncate">{contact.message}</p>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell text-gray-500 text-xs whitespace-nowrap">
                      {formatDate(contact.createdAt)}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleWhatsApp(contact)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-semibold transition-colors"
                          title={`WhatsApp ${contact.phone}`}
                        >
                          <FiMessageCircle className="w-3.5 h-3.5" />
                          Contact
                        </button>
                        <button
                          onClick={() => handleDelete(contact._id)}
                          disabled={deletingId === contact._id}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {paginated.length === 0 && !loading && (
            <div className="text-center py-16">
              <FiMessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No contact messages yet</p>
              <p className="text-gray-400 text-sm mt-1">Messages submitted through the contact form will appear here.</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-50">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 hover:border-primary-400 disabled:opacity-40"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-3">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-gray-200 hover:border-primary-400 disabled:opacity-40"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContacts;
